<template>
	<div class="w-full h-full z-0" ref="mapContainer"></div>
</template>

<script setup lang="ts">
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
	zoom: {
		type: Number,
		default: 18,
		validator(value) {
			return Number.isInteger(value)
		}
	},
	minZoom: {
		type: Number,
		default: 5,
		validator(value) {
			return Number.isInteger(value)
		}
	},
	maxZoom: {
		type: Number,
		default: 18,
		validator(value) {
			return Number.isInteger(value)
		}
	},
	center: {
		type: Object,
		default: null
	},
	markers: {
		type: Array,
		default: [],
		validator(value) {
			return Array.isArray(value)
		}
	},
	userMarker: {
		type: Object,
		default: null
	},
	circle: {
		type: Object,
		default: null
	},
	dragging: {
		type: Boolean,
		default: false
	},
	tap: {
		type: Boolean,
		default: false
	},
	zoomControl: {
		type: Boolean,
		default: false
	},
	keyboard: {
		type: Boolean,
		default: false
	},
	boxZoom: {
		type: Boolean,
		default: false
	},
	doubleClickZoom: {
		type: Boolean,
		default: false
	},
	scrollWheelZoom: {
		type: Boolean,
		default: true
	},
	touchZoom: {
		type: Boolean,
		default: false
	}
})

const mapContainer = ref(null)
const map = ref(null)
const emit = defineEmits(['click', 'zoom', 'ready'])
onMounted(() => {
	// Usar un centro por defecto si no se proporciona uno
	const defaultCenter = props.center || [40.4168, -3.7038] // Madrid, España como fallback
	const defaultZoom = props.center ? props.zoom : 16 // Zoom más amplio si no hay centro específico

	map.value = L.map(mapContainer.value, {
		minZoom: props.minZoom,
		maxZoom: props.maxZoom,
		zoom: defaultZoom,
		dragging: props.dragging,
		tap: props.tap,
		zoomControl: props.zoomControl,
		keyboard: props.keyboard,
		boxZoom: props.boxZoom,
		doubleClickZoom: props.doubleClickZoom,
		scrollWheelZoom: props.scrollWheelZoom,
		touchZoom: props.touchZoom,
		center: defaultCenter
	})

	map.value.on('click', function (e) {
		emit('click', { lat: e.latlng.lat, lng: e.latlng.lng })
	})

	L.tileLayer('https://{s}.tile.osm.org/{z}/{x}/{y}.png', {
		attribution: ''
	}).addTo(map.value)

	map.value.on('zoomend', function (e) {
		emit('zoom', map.value.getZoom())
	})
	map.value.whenReady(() => {
		props.markers.forEach((marker: Record<string, any>) => {
			marker.addTo(map.value)
			// marker.bindPopup('Popup content')
			marker.on('mouseover', function (e) {
				this.openPopup()
			})
			marker.on('mouseout', function (e) {
				this.closePopup()
			})
		})
		emit('ready')
	})
})

onUnmounted(() => {
	// Limpiar todos los marcadores antes de destruir el mapa
	props.markers.forEach((marker: Record<string, any>) => {
		if (marker && map.value) {
			try {
				map.value.removeLayer(marker)
			} catch (error) {
				// Ignorar errores de marcadores ya eliminados
				console.warn('Error al eliminar marcador:', error)
			}
		}
	})

	// Limpiar el mapa de forma segura
	if (map.value) {
		try {
			map.value.remove()
		} catch (error) {
			console.warn('Error al eliminar mapa:', error)
		}
		map.value = null
	}
})

watch(
	() => props.markers,
	(markers, old) => {
		// Verificar que el mapa existe antes de operar
		if (!map.value) return

		// Eliminar marcadores antiguos de forma segura
		old?.forEach(marker => {
			if (marker && map.value) {
				try {
					map.value.removeLayer(marker)
				} catch (error) {
					console.warn('Error al eliminar marcador antiguo:', error)
				}
			}
		})

		// Añadir nuevos marcadores de forma segura
		markers?.forEach((marker: Record<string, any>) => {
			if (marker && map.value) {
				try {
					marker.addTo(map.value)
				} catch (error) {
					console.warn('Error al añadir marcador:', error)
				}
			}
		})
	}
)

watch(
	() => props.userMarker,
	marker => {
		if (marker && map.value && !marker._map) {
			try {
				marker.addTo(map.value)
			} catch (error) {
				console.warn('Error al añadir marcador de usuario:', error)
			}
		}
	}
)

watch(
	() => props.circle,
	item => {
		if (item && map.value && !item._map) {
			try {
				item.addTo(map.value)
			} catch (error) {
				console.warn('Error al añadir círculo:', error)
			}
		}
	}
)

watch(
	() => props.center,
	center => {
		if (center && map.value) {
			try {
				map.value.setView(center, map.value.getZoom())
			} catch (error) {
				console.warn('Error al establecer centro del mapa:', error)
			}
		}
	}
)
</script>
